import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { requestScrollUpdate } from './scroll';
import './responsive.css';

/*
 * The site is designed as a series of fixed 1366px-wide compositions.
 * On screens narrower than that (tablets, small laptop windows) we scale the
 * whole design down to fit the width, so every section keeps its exact layout.
 * At 1462px (DESIGN_WIDTH + 2 × SIDE_MARGIN) and wider the scale is 1 and nothing changes.
 * To make the margins bigger or smaller later, change SIDE_MARGIN;
 *
 * CSS variables set on <html>, for use in stylesheets:
 *   --site-scale  scale applied to the page content
 *   --nav-scale   scale applied to the fixed navbar (kept inside the screen edges)
 * and the attribute `data-scaled` on <html> whenever the page is scaled down.
 */

export const DESIGN_WIDTH = 1366;
// Minimum empty space kept on each side of the 1366px design, in design pixels.
// Desktop screens always had this naturally; scaled screens now get it too.
export const SIDE_MARGIN = 48;
const NAV_WIDTH = 1379;
const NAV_SIDE_GAP = 12;

const readScale = () => {
  const width = document.documentElement.clientWidth || window.innerWidth;
  return {
    site: Math.min(1, width / (DESIGN_WIDTH + SIDE_MARGIN * 2)),
    nav: Math.min(1, (width - NAV_SIDE_GAP * 2) / NAV_WIDTH),
  };
};

const applyScaleVars = ({ site, nav }) => {
  const root = document.documentElement.style;
  root.setProperty('--site-scale', String(site));
  root.setProperty('--nav-scale', String(nav));
  if (site < 1) document.documentElement.setAttribute('data-scaled', '');
  else document.documentElement.removeAttribute('data-scaled');
};

export const useSiteScale = () => {
  const [scale, setScale] = useState(() => {
    const s = readScale();
    applyScaleVars(s);
    return s.site;
  });

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const s = readScale();
      applyScaleVars(s);
      setScale(s.site);
    };
    const onResize = () => {
      if (frame == null) frame = requestAnimationFrame(update);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, []);

  return scale;
};

/**
 * Wraps the page sections. Uses a transform (not CSS `zoom`) because it
 * behaves the same in Safari, Chrome and Firefox, including for
 * getBoundingClientRect and IntersectionObserver.
 * A transform doesn't change layout size, so the outer frame is given the
 * scaled height explicitly.
 */
const ScaledCanvas = ({ children }) => {
  const scale = useSiteScale();
  const innerRef = useRef(null);
  const [innerHeight, setInnerHeight] = useState(null);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return undefined;
    const measure = () => {
      setInnerHeight(el.offsetHeight);
      requestScrollUpdate();
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scaled = scale < 1;

  return (
    <div
      className="site-canvas-frame"
      style={scaled && innerHeight != null ? { height: innerHeight * scale } : undefined}
    >
      <div
        ref={innerRef}
        className="site-canvas"
        style={scaled ? { transform: `scale(${scale})` } : undefined}
      >
        {children}
      </div>
    </div>
  );
};

export default ScaledCanvas;
